<template>
  <!-- id="draggable"
  class="draggable select-none card" -->
  <!-- :style="{ position: 'fixed', left: posX + 'px', top: posY + 'px' }" -->
  <div
    id="draggable"
    class="draggable select-none card"
    :class="showNotes ? 'd-block' : 'd-none'"
    :style="{ position: 'fixed', left: posX + 'px', top: posY + 'px' }"
  >
    <div id="draggable-handle" class="draggable-border cursor-move px-5 py-5">
      <div class="d-flex justify-content-between align-items-center">
        <h4 class="mb-0">
          {{ $t('resultDetails.notes.draggable.title') }}
        </h4>
        <button class="btn btn-link p-0" @click="toggleNotes">
          <i class="bi-x p-2 fs-3"> </i>
        </button>
      </div>
    </div>
    <div class="p-5">
      <div>
        <LoadingSpinner v-if="notesLoading" wrapperClass="mt-5 mb-5" />
        <div v-else>
          <div v-for="noteName in Object.keys(noteTexts)" :key="noteName">
            <VueEditor
              v-model="noteTexts[noteName].value"
              :editor-toolbar="customEditorToolbar"
              :placeholder="$t('resultDetails.notes.draggable.placeholder')"
            />
            <div class="draggable-footer d-flex justify-content-between mt-5">
              <div>
                <span class="text-muted">
                  <i class="bi-lock-fill"></i>
                  {{ $t('resultDetails.notes.draggable.explanation') }}
                </span>
              </div>
              <div>
                <div v-if="!errorMessageForNote(noteName)">
                  <span
                    v-if="shouldDisplayInitialHelp(noteName)"
                    class="text-muted"
                  >
                    {{ $t('resultDetails.notes.status.initial') }}
                  </span>
                  <span v-if="hasUnsavedChanges(noteName)" class="text-muted">
                    {{ $t('resultDetails.notes.status.unsaved') }}
                  </span>
                  <span v-if="isSavingChanges(noteName)" class="text-muted">
                    <div
                      class="spinner-border spinner-border-sm text-muted"
                    ></div>
                  </span>
                  <span
                    v-if="isSuccessfullySaved(noteName)"
                    class="text-success"
                  >
                    {{ $t('resultDetails.notes.status.saved') }}
                  </span>
                </div>
                <div v-else>
                  <p
                    v-if="errorMessageForNote(noteName)"
                    class="m-0 d-flex align-items-center"
                  >
                    <i
                      class="bi-exclamation-octagon-fill text-danger me-2 fs-5"
                    ></i>
                    <span class="text-danger">
                      {{ $t(errorMessageForNote(noteName)) }}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useNotesStore } from '@/stores/notes-store'
import { requestTypes } from '@/constants/request-types'
import { useDraggable } from '@vueuse/core'
import { VueEditor } from 'vue3-editor'

export default {
  name: 'Notebook',
  components: {
    VueEditor
  },
  props: {
    surveyName: {
      type: String,
      default: ''
    },
    teamForSelectedGroup: {
      type: String,
      default: () => {}
    },
    organizationName: {
      type: String,
      default: ''
    },
    showNotes: {
      type: Boolean,
      default: false
    },
    username: {
      type: String,
      default: ''
    }
  },
  emits: ['notesClosed'],
  setup() {
    const notesStore = useNotesStore()
    return {
      notesStore
    }
  },
  data() {
    return {
      customEditorToolbar: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote'],

        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],

        [{ color: [] }, { background: [] }],
        ['clean']
      ],
      noteTexts: {},
      x: null,
      y: null
    }
  },
  computed: {
    createUpdateNoteLoading() {
      return this.notesStore.createUpdateNoteLoading
    },
    notes() {
      return this.notesStore.notes
    },
    notesLoading() {
      return this.notesStore.loading[requestTypes.NOTES]
    },
    posX() {
      return this.x
    },
    posY() {
      return this.y
    },
    teamNameForNote() {
      return this.teamForSelectedGroup || this.organizationName
    }
  },
  watch: {
    notes() {
      this.setNoteTexts()
    },
    teamForSelectedGroup() {
      this.setupNotesForTeam()
    }
  },
  mounted() {
    this.setupDraggable()
    this.setupNotesForTeam()
    window.addEventListener('resize', this.handleWindowResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleWindowResize)
    clearInterval(this.timerId)
  },
  methods: {
    addNewNote(e) {
      if (e) {
        e.preventDefault()
      }
      const initialNoteName = this.notes[0].name
      const to = []
      this.notesStore.createSurveyNoteAction({
        teamName: this.teamNameForNote,
        surveyName: this.surveyName,
        noteText: this.noteTexts[initialNoteName].value,
        username: this.username,
        noteName: initialNoteName,
        to
      })
      this.newNoteText = ''
    },
    deleteNote(noteName) {
      this.noteTexts = Object.keys(this.noteTexts).reduce((res, n) => {
        if (n !== noteName) {
          res[n] = this.noteTexts[n]
        }
        return res
      }, {})
      this.newNoteText = ''
      this.notesStore.deleteNoteAction({
        teamName: this.teamNameForNote,
        surveyName: this.surveyName,
        noteName
      })
    },
    // todo: make errorMessageForNote and hasUnsavedChanges into computed property
    errorMessageForNote(noteName) {
      return this.notesStore.error && this.notesStore.error[noteName]
    },
    hasUnsavedChanges(noteName) {
      return (
        !this.shouldDisplayInitialHelp(noteName) &&
        (this.noteTextHasChanged(noteName) ||
          this.notePrivacyHasChanged(noteName)) &&
        !this.createUpdateNoteLoading
      )
    },
    isSuccessfullySaved(noteName) {
      return (
        !this.shouldDisplayInitialHelp(noteName) &&
        !this.hasUnsavedChanges(noteName) &&
        !this.createUpdateNoteLoading
      )
    },
    notePrivacyHasChanged(noteName) {
      const noteFound = this.notes?.find((n) => n.name === noteName)
      if (noteFound) {
        const note = noteFound
        const noteIsPrivate = this.noteIsPrivate(note)
        const updatedNoteIsPrivate = this.privateSelected(note.name)
        return noteIsPrivate !== updatedNoteIsPrivate
      }
      return false
    },
    noteTextHasChanged(noteName) {
      if (this.noteTexts[noteName]) {
        const latestNoteText = this.noteTexts[noteName].value
        const oldNotesFound = this.notes?.find((n) => n.name === noteName)
        if (oldNotesFound) {
          const oldNoteText = oldNotesFound.value
          return oldNoteText !== latestNoteText
        }
      }
      return false
    },
    noteShouldBeDeleted(noteName) {
      const noteFound = this.notes.find((n) => n.name === noteName)
      if (noteFound) {
        const noteText = this.noteTexts[noteName]?.value
        return (
          !noteFound.initial &&
          !this.createUpdateNoteLoading &&
          typeof noteText === 'string' &&
          noteText === ''
        )
      }
      return false
    },
    privateSelected(noteName) {
      return this.noteTexts?.[noteName]?.isPrivate
    },
    saveNoteIfNeeded() {
      if (!this.createUpdateNoteLoading && this.notes && this.notes.length) {
        this.notes.forEach((note) => {
          if (this.hasUnsavedChanges(note.name)) {
            if (
              this.noteTexts[note.name] &&
              this.noteTexts[note.name].value.length > 1
            ) {
              if (note.initial) {
                this.addNewNote()
              } else {
                this.updateNote(note.name)
              }
            } else if (this.noteShouldBeDeleted(note.name)) {
              this.deleteNote(note.name)
            } else {
            }
          }
        })
      }
    },
    setupDraggable() {
      const draggable = document.getElementById('draggable')
      const handle = document.getElementById('draggable-handle')
      const { x, y } = useDraggable(draggable, {
        initialValue: { x: 10, y: 50 },
        handle
      })
      this.x = x
      this.y = y
    },
    setupNotesForTeam() {
      this.loadSurveyNotes({
        teamName: this.teamNameForNote
      })
      this.setNoteTexts()
      this.timerId = setInterval(this.saveNoteIfNeeded, 3000)
    },
    setNoteTexts() {
      if (this.notes) {
        if (this.notes.length === 0) {
          // if there are no notes then create a local empty one
          // to use until its saved on server
          this.notesStore.createInitialSurveyNoteAction({
            username: this.username
          })
        } else {
          this.noteTexts = this.notes.reduce((res, note) => {
            res[note.name] = {
              value: note.value,
              isPrivate: this.noteIsPrivate(note)
            }
            return res
          }, {})
        }
      }
    },
    shouldDisplayInitialHelp(noteName) {
      const noteFound =
        this.notes?.length && this.notes.find((note) => note.name === noteName)
      return (
        noteFound && noteFound.initial && this.noteTexts[noteName]?.value === ''
      )
    },
    toggleNotes() {
      this.$emit('notesClosed')
    },
    updateNote(noteName) {
      const to = []
      const noteText = this.noteTexts[noteName].value
      this.notesStore.updateSurveyNoteAction({
        teamName: this.teamNameForNote,
        surveyName: this.surveyName,
        noteName,
        noteText,
        to
      })
    },
    isSavingChanges() {
      return this.createUpdateNoteLoading
    },
    handleWindowResize() {
      // If the notes window is invisible due to position
      if (this.posX > window.innerWidth) {
        this.setupDraggable()
      }
    },
    loadSurveyNotes({ teamName }) {
      this.notesStore.loadSurveyNotesAction({
        teamName,
        surveyName: this.surveyName
      })
    },
    noteIsPrivate(note) {
      return note.to && note.to.length === 0
    }
  }
}
</script>

<style lang="scss">
.ql-editor {
  height: 35rem !important;
  cursor: text;
  &::before {
    color: var(--kt-input-placeholder-color) !important;
  }
}
.quillWrapper .ql-toolbar {
  border-top-left-radius: 0.95rem;
  border-top-right-radius: 0.95rem;
  background-color: var(--kt-input-solid-bg);
  border: 1px solid var(--kt-input-border-color);
  button:hover,
  .ql-picker-label:hover {
    color: var(--kt-primary) !important;
    .ql-stroke {
      stroke: var(--kt-primary) !important;
    }
    .ql-fill {
      fill: var(--kt-primary) !important;
    }
  }
}

.quillWrapper .ql-container {
  border-bottom-left-radius: 0.95rem;
  border-bottom-right-radius: 0.95rem;
  background-color: var(--kt-input-bg);
  border: 1px solid var(--kt-input-border-color);
}
.quillWrapper:focus-within .ql-toolbar {
  border-color: var(--kt-input-focus-border-color);
  transition: all 0.2s ease;
}
.quillWrapper:focus-within .ql-container {
  background-color: var(--kt-input-focus-bg);
  border-color: var(--kt-input-focus-border-color);
  transition: all 0.2s ease;
}
</style>
<style scoped lang="scss">
.draggable-border {
  border-top-left-radius: 0.95rem;
  border-top-right-radius: 0.95rem;
  background-color: var(--kt-gray-300);
}
.export-option-button:hover {
  background-color: var(--kt-gray-200) !important;
}
.draggable {
  max-width: 592px;
  user-select: none;
  touch-action: none;
  z-index: 2;
  box-shadow: var(--kt-feedback-popup-box-shadow);
  // z-index: 10001; // Since modals have 10000. This doesn't work tho - must find a way to make editor have focus.
}
</style>
